import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface PollOption {
  id: number;
  optionText: string;
  voteCount: number;
  percentage: number;
}

interface PollData {
  id: number;
  question: string;
  options: PollOption[];
  totalVotes: number;
  userVote?: number | null;
}

interface PollDisplayProps {
  poll: PollData;
  onVote?: (optionId: number) => Promise<void>;
  isAuthenticated?: boolean;
}

const PollDisplay: React.FC<PollDisplayProps> = ({ poll, onVote, isAuthenticated = false }) => {
  const [isVoting, setIsVoting] = useState(false);
  const [localPoll, setLocalPoll] = useState(poll);
  const [hoveredOption, setHoveredOption] = useState<number | null>(null);

  const handleVote = async (optionId: number) => {
    if (!onVote || !isAuthenticated || isVoting) return;

    setIsVoting(true);
    try {
      await onVote(optionId);
      // Update local state optimistically
      setLocalPoll(prev => {
        const newOptions = prev.options.map(option => {
          if (option.id === optionId) {
            // Add vote to this option if user hasn't voted, or move vote here
            const newVoteCount = prev.userVote === optionId ? option.voteCount : option.voteCount + 1;
            return { ...option, voteCount: newVoteCount };
          } else if (prev.userVote === option.id) {
            // Remove vote from previous option
            return { ...option, voteCount: Math.max(0, option.voteCount - 1) };
          }
          return option;
        });

        const newTotalVotes = prev.userVote ? prev.totalVotes : prev.totalVotes + 1;
        
        // Recalculate percentages
        const optionsWithPercentages = newOptions.map(option => ({
          ...option,
          percentage: newTotalVotes > 0 ? (option.voteCount / newTotalVotes) * 100 : 0
        }));

        return {
          ...prev,
          userVote: optionId,
          options: optionsWithPercentages,
          totalVotes: newTotalVotes
        };
      });
    } catch (error) {
      console.error('Error voting:', error);
    } finally {
      setIsVoting(false);
    }
  };

  // Enhanced color palette with gradients
  const colorSchemes = [
    { 
      bg: 'from-blue-500 to-blue-600', 
      border: 'border-blue-500', 
      text: 'text-blue-200',
      light: 'bg-blue-50 dark:bg-blue-900/20',
      hover: 'hover:from-blue-600 hover:to-blue-700'
    },
    { 
      bg: 'from-emerald-500 to-emerald-600', 
      border: 'border-emerald-500', 
      text: 'text-emerald-300',
      light: 'bg-emerald-50 dark:bg-emerald-900/20',
      hover: 'hover:from-emerald-600 hover:to-emerald-700'
    },
    { 
      bg: 'from-purple-500 to-purple-600', 
      border: 'border-purple-500', 
      text: 'text-purple-300',
      light: 'bg-purple-50 dark:bg-purple-900/20',
      hover: 'hover:from-purple-600 hover:to-purple-700'
    },
    { 
      bg: 'from-rose-500 to-rose-600', 
      border: 'border-rose-500', 
      text: 'text-rose-300',
      light: 'bg-rose-50 dark:bg-rose-900/20',
      hover: 'hover:from-rose-600 hover:to-rose-700'
    },
    { 
      bg: 'from-amber-500 to-amber-600', 
      border: 'border-amber-500', 
      text: 'text-amber-300',
      light: 'bg-amber-50 dark:bg-amber-900/20',
      hover: 'hover:from-amber-600 hover:to-amber-700'
    },
    { 
      bg: 'from-indigo-500 to-indigo-600', 
      border: 'border-indigo-500', 
      text: 'text-indigo-300',
      light: 'bg-indigo-50 dark:bg-indigo-900/20',
      hover: 'hover:from-indigo-600 hover:to-indigo-700'
    }
  ];

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <div>
          <span className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">نظرسنجی</span>
          <div className="text-xs text-gray-500 dark:text-gray-100">
            {localPoll.totalVotes === 0 ? (
              'اولین نفری باشید که رای می‌دهد'
            ) : (
              `${localPoll.totalVotes.toLocaleString('fa-IR')} رای`
            )}
          </div>
        </div>
      </div>

      {/* Question */}
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 leading-relaxed">
        {localPoll.question}
      </h3>

      {/* Options */}
      <div className="space-y-4">
        {localPoll.options.map((option, index) => {
          const colorScheme = colorSchemes[index % colorSchemes.length];
          const isSelected = localPoll.userVote === option.id;
          const isHovered = hoveredOption === option.id;
          
          return (
            <motion.div 
              key={option.id} 
              className="relative overflow-hidden"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <button
                onClick={() => handleVote(option.id)}
                disabled={!isAuthenticated || isVoting}
                onMouseEnter={() => setHoveredOption(option.id)}
                onMouseLeave={() => setHoveredOption(null)}
                className={`w-full text-start p-4 rounded-xl border-2 transition-all duration-300 relative overflow-hidden group ${
                  isSelected
                    ? `${colorScheme.border} ${colorScheme.light} shadow-lg`
                    : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-md'
                } ${
                  isAuthenticated && !isVoting
                    ? 'cursor-pointer'
                    : 'cursor-default opacity-75'
                }`}
              >
                {/* Background progress bar */}
                {localPoll.totalVotes > 0 && (
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${option.percentage}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className={`absolute top-0 right-0 h-full bg-gradient-to-l ${colorScheme.bg} opacity-10 rounded-xl`}
                  />
                )}

                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Vote count badge */}
                    <motion.div 
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        isSelected ? `${colorScheme.text} bg-white dark:bg-gray-800` : 'text-gray-500 bg-gray-100 dark:bg-gray-700'
                      }`}
                      animate={{ scale: isSelected ? 1.1 : 1 }}
                    >
                      {Math.round(option.percentage)}%
                    </motion.div>
                    
                    {/* Selected indicator */}
                    <AnimatePresence>
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          exit={{ scale: 0, rotate: 180 }}
                          className={`p-1 rounded-full bg-gradient-to-r ${colorScheme.bg}`}
                        >
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Voting indicator */}
                    {isVoting && hoveredOption === option.id && (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full"
                      />
                    )}
                  </div>

                  <div className="flex-1 me-4">
                    <span className={`text-lg font-medium ${
                      isSelected ? colorScheme.text : 'text-gray-900 dark:text-white'
                    }`}>
                      {option.optionText}
                    </span>
                    
                    {/* Vote count */}
                    <div className="text-sm text-gray-500 dark:text-gray-100 mt-1">
                      {option.voteCount.toLocaleString('fa-IR')} رای
                    </div>
                  </div>
                </div>

                {/* Hover effect */}
                {isHovered && isAuthenticated && !isVoting && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`absolute inset-0 bg-gradient-to-r ${colorScheme.bg} opacity-5 rounded-xl`}
                  />
                )}
              </button>
            </motion.div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        {!isAuthenticated ? (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg text-sm font-medium">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              برای رای دادن وارد شوید
            </div>
          </div>
        ) : (
          <div className="text-center text-sm text-gray-600 dark:text-gray-100">
            {localPoll.userVote ? (
              <span className="text-green-600 dark:text-green-400 font-medium">
                ✓ شما رای داده‌اید - می‌توانید تغییر دهید
              </span>
            ) : (
              'گزینه مورد نظر خود را انتخاب کنید'
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PollDisplay;